import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import AppModal from '../../../../../generic/ui/Modal/AppModal'
import ConfirmDialog from '../../../../../generic/ui/ConfirmDialog/ConfirmDialog'
import { GridTable, TableToolbar } from '../../../../../generic/grid-table'
import { GridColumn } from '../../../../../generic/grid-table/types/grid.types'
import ProductForm from './components/ProductForm'
import useTableData from '../../../../../generic/universal-table/hooks/useTableData'
import useTableEditing from '../../../../../generic/universal-table/hooks/useTableEditing'
import useTableFilters from '../../../../../generic/universal-table/hooks/useTableFilters'
import { ColumnDefinition } from '../../../../../generic/universal-table/types/column.types'
import { FormFieldDefinition } from '../../../../../generic/universal-table/types/form.types'
import { HARDCODED_CALCULATION_METHODS } from '../../../../../generic/artcoded/calculationMethod'
import useExportToExcel from '../../../../../generic/hooks/useExportToExcel'
import columnsToExcel from '../../../../../generic/universal-table/utils/columnsToExcel'
import { productTableService } from './services/productTable.service'
import { productTableColumns } from './configs/productTable.columns'
import { productTableFormFields } from './configs/productTable.form'
import { productTableAdapter } from './adapters/productTable.adapter'
import { ProductCreatePayload, ProductUiRow, ProductUpdatePayload } from './types/productTable.types'
import useDataFetcher from '../../knisot/hooks/useFetchData'
import { IGeneralProductId } from '../../../../../modules/API/Products/models/generalProductId'
import { Product } from '../../../../../modules/API/Products/products'

const TableSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8em;
  width: 100%;
  height: 100%;
  padding: 0.5em;
  overflow: hidden;
`

const ErrorMessage = styled.p`
  color: tomato;
  direction: rtl;
  padding: 0.5em 1em;
`

const ProductTableModule = () => {
  const {
    rows,
    isLoading,
    error,
    createRow,
    updateRow,
    removeRow,
  } = useTableData<ProductUiRow, ProductCreatePayload, ProductUpdatePayload>(
    productTableService,
    productTableAdapter.getRowId
  )

  const {
    isFormOpen,
    editingMode,
    selectedRow,
    handleOpenCreate,
    handleOpenEdit,
    handleCloseForm,
    isConfirmDeleteOpen,
    isDeleting,
    handleRequestDelete,
    handleCancelDelete,
    handleConfirmDelete,
  } = useTableEditing<ProductUiRow>()

  const { searchQuery, filteredRows, handleSearch } = useTableFilters<ProductUiRow>(rows)

  const {
    data: generalProductIdList,
  } = useDataFetcher<IGeneralProductId[]>(Product.getGeneralProductIdList, "data")

  const calculationMethodOptions = useMemo(
    () =>
      HARDCODED_CALCULATION_METHODS.map((item) => ({
        value: item.id,
        label: item.name,
      })),
    []
  )
  const generalProductIdOptions = useMemo(
    () =>
      generalProductIdList?.map((o) => ({ value: o.ProductId, label: o.Description })) ?? [],
    [generalProductIdList]
  )
  const resolveCalculationMethodLabel = useCallback(
    (id: unknown) =>
      calculationMethodOptions.find((o) => o.value === id)?.label ?? String(id),
    [calculationMethodOptions]
  )

  const resolveProductIdLabel = useCallback(
    (id: unknown) =>
      generalProductIdList?.find((o) => o.ProductId === id)?.Description ?? String(id),
    [generalProductIdList]
  )

  const enrichedColumns = useMemo<ColumnDefinition<ProductUiRow>[]>(
    () =>
      productTableColumns.map((col) => {
        if (col.key === 'CalculationMethod') {
          return {
            ...col,
            labelResolver: resolveCalculationMethodLabel,
          }
        }
        if (col.key === 'GeneralProductID') {
          return {
            ...col,
            labelResolver: resolveProductIdLabel,
          }
        }
        return col
      }),
    [resolveCalculationMethodLabel]
  )

  const enrichedFormFields = useMemo<FormFieldDefinition<ProductUiRow>[]>(
    () =>
      productTableFormFields.map((field) => {
        if (field.name === 'CalculationMethod') {
          return { ...field, getOptions: () => calculationMethodOptions }
        }
        if (field.name === 'GeneralProductID') {
          return { ...field, getOptions: () => generalProductIdOptions }
        }
        return field
      }),
    [calculationMethodOptions, generalProductIdOptions]
  )

  // Grid view reuses the enriched columns and layers on richer renderers.
  const gridColumns = useMemo<GridColumn<ProductUiRow>[]>(
    () =>
      enrichedColumns.map((col): GridColumn<ProductUiRow> => {
        if (col.key === 'ProductID') return { ...col, cellRendererType: 'num' }
        return col
      }),
    [enrichedColumns]
  )

  const excelColumns = useMemo(() => columnsToExcel(enrichedColumns), [enrichedColumns])
  const { handleExport } = useExportToExcel({ columns: excelColumns, fileName: 'products', rtl: true })

  const [isSaving, setIsSaving] = useState<boolean>(false)

  const handleFormSubmit = useCallback(
    async (values: Partial<ProductUiRow>): Promise<void> => {
      setIsSaving(true)
      try {
        if (editingMode === 'edit' && selectedRow) {
          const id = productTableAdapter.getRowId(selectedRow)
          const payload = productTableAdapter.toUpdatePayload({
            ...selectedRow,
            ...values,
          } as ProductUiRow)
          await updateRow(id, payload)
        } else {
          const payload = productTableAdapter.toCreatePayload(
            values as ProductUiRow
          )
          await createRow(payload)
        }
        handleCloseForm()
      } finally {
        setIsSaving(false)
      }
    },
    [editingMode, selectedRow, createRow, updateRow, handleCloseForm]
  )

  const formTitle =
    editingMode === 'edit' ? 'עריכת פריט' : 'הוספת פריט חדש'

  return (
    <TableSection>
      {isFormOpen && (
        <AppModal
          title={formTitle}
          controlFunc={handleCloseForm}
          Children={ProductForm}
          childrenProps={{
            fields: enrichedFormFields,
            initialValues: selectedRow ?? undefined,
            onSubmit: handleFormSubmit,
            onDelete: editingMode === 'edit' ? handleRequestDelete : undefined,
            isLoading: isSaving,
            isDeleting,
            submitLabel: editingMode === 'edit' ? 'עדכן' : 'הוסף',
          }}
          size="md"
        />
      )}

      <ConfirmDialog
        open={isConfirmDeleteOpen}
        title="מחיקת פריט"
        message="האם למחוק את הפריט? פעולה זו אינה הפיכה."
        isLoading={isDeleting}
        onCancel={handleCancelDelete}
        onConfirm={() =>
          handleConfirmDelete((row) => removeRow(productTableAdapter.getRowId(row)))
        }
      />

      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <GridTable<ProductUiRow>
        title="פריטים"
        count={filteredRows.length}
        columns={gridColumns}
        rows={filteredRows}
        getRowId={productTableAdapter.getRowId}
        isLoading={isLoading}
        onRowClick={handleOpenEdit}
        toolbarActions={
          <TableToolbar
            searchValue={searchQuery}
            onSearchChange={handleSearch}
            searchPlaceholder="חיפוש פריט…"
            onExport={() => handleExport(filteredRows)}
            onAdd={handleOpenCreate}
            addLabel="הוסף פריט"
          />
        }
      />
    </TableSection>
  )
}

export default ProductTableModule
